import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { templateService } from '../../services/api';
import { Template } from '../../types';
import TemplateCard from './TemplateCard';
import CreateTemplateModal from './CreateTemplateModal';

const Dashboard: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      const data = await templateService.getTemplates();
      setTemplates(data);
    } catch (err: any) {
      setError('Template\'lar yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTemplateCreated = (template: Template) => {
    setTemplates([template, ...templates]);
    setShowCreateModal(false);
  };

  const handleTemplateDeleted = (templateId: string) => {
    setTemplates(templates.filter(t => t._id !== templateId));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            🎨 Template Dashboard
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Create stunning visual templates with our modern drag & drop editor
          </p>
        </div>
        
        {error && (
          <div className="mb-8 bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl shadow-sm">
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-4 px-8 rounded-xl text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            ✨ Create New Template
          </button>
          <Link
            to="/editor"
            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold py-4 px-8 rounded-xl text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 text-center"
          >
            🚀 Start from Scratch
          </Link>
        </div>

        {/* Templates Grid */}
        {templates.length === 0 ? (
          <div className="text-center py-16">
            <div className="bg-white rounded-2xl shadow-lg p-12 max-w-md mx-auto">
              <div className="text-gray-400 text-8xl mb-6">📄</div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                No templates yet
              </h3>
              <p className="text-gray-600 mb-6">
                Create your first template to get started
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
              >
                Create First Template
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {templates.map((template) => (
              <TemplateCard
                key={template._id}
                template={template}
                onDelete={handleTemplateDeleted}
              />
            ))}
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateTemplateModal
          onClose={() => setShowCreateModal(false)}
          onTemplateCreated={handleTemplateCreated}
        />
      )}
    </div>
  );
};

export default Dashboard;
